import { CompositeFilterDescriptor, getter, State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  getSelectedState,
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridRowDoubleClickEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { documentAttachmentColumns } from 'src/assets/static/enter_invoice/documentAttachmentColumns';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import GridWrapper from 'src/components/GridWrapper';
import useAttachDocument from 'src/hooks/useAttachDocument';
import useConfirm from 'src/hooks/useConfirm';
import InvoiceGridToolbar from '../enter_invoice/InvoiceGridToolBar';

export const initialScanManagerDataState = (isInvoice: boolean): State => {
  let initialFilters: Array<CompositeFilterDescriptor> = [
    {
      logic: 'and',
      filters: [{ field: 'receiptId', value: '', operator: 'isnull' }],
    },
  ];
  if (isInvoice) {
    initialFilters = [
      ...initialFilters,
      {
        logic: 'and',
        filters: [{ field: 'ocrStatus', value: '', operator: 'isnullorempty' }],
      },
      {
        logic: 'and',
        filters: [{ field: 'isDeleted', value: 'false', operator: 'eq' }],
      },
    ];
  }
  return {
    skip: 0,
    take: 20,
    sort: [
      {
        field: 'originalFile',
        dir: 'asc',
      },
    ],

    filter: {
      logic: 'and',
      filters: initialFilters,
    },
  };
};

const DATA_ITEM_KEY = 'attachId';
const idGetter = getter(DATA_ITEM_KEY);

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 2,
  pageSizes: [5, 10, 20, 50, 100],
};
const defaultProps = {
  tableTextId: 'Records',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface IProps {
  openModal: boolean;
  closeModal: () => void;
  title: string;
  noOfAttachments?: number;
  isNewDocument?: boolean;
  hasPoMpa?: boolean;
}

const multipleAttachmentsContent = (
  <div>
    This receipt has documents attached to it. Are you sure you want to attach additional documents
    to this receipt?
  </div>
);

const AttachDocumentWizard: React.FC<IProps> = ({
  openModal,
  closeModal,
  title,
  noOfAttachments,
  isNewDocument,
  hasPoMpa,
}) => {
  const { confirm, onClose } = useConfirm();
  const {
    dataState,
    setDataState,
    getAttachDocuments,
    count,
    attachmentListData,
    setAttachmentListData,
    isLoading,
    handleAttachment,
    handleWithoutAttachment,
    isRowSelected,
    isInvoice,
    isExistingDocument,
    isAttachmentButtonLoading,
    selectedAttachments,
  } = useAttachDocument();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (openModal) {
        getAttachDocuments();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [getAttachDocuments, openModal]);

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const [selectedState, setSelectedState] = useState<{
    [id: string]: boolean | number[];
  }>({});

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const addSelAttachments: Record<number, boolean> = selectedAttachments?.reduce(
        (acc, value) => {
          acc[value] = true;
          return acc;
        },
        {} as Record<number, boolean>
      );

      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });

      const mergedState =
        selectedAttachments?.length > 0
          ? {
              ...newSelectedState,
              ...addSelAttachments,
            }
          : newSelectedState;

      setSelectedState(mergedState);
      setAttachmentListData((prev: any) => {
        const selectedPOList = prev.map((item: any) => ({
          ...item,
          selected: mergedState[idGetter(item)],
        }));
        return selectedPOList;
      });
    },
    [selectedAttachments, selectedState, setAttachmentListData]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const { checked } = event.syntheticEvent.target as any;
      setAttachmentListData((prev) => [
        ...[...prev].map((item) => ({
          ...item,
          selected: checked,
        })),
      ]);
    },
    [setAttachmentListData]
  );

  // clear filter;
  const handleClearFilter = useCallback(() => {
    setDataState(initialScanManagerDataState(isInvoice));
  }, [isInvoice, setDataState]);

  const handleAttachDocumentWithMpa = useCallback(
    async (handleAttachment: Function) => {
      const isSinglePeriodAccepted = await confirm({
        title: 'Multiple Period Allocation',
        content:
          'The PO associated with this Invoice is assigned to Multiple Periods. Please choose either Multiple or Single Period Assignment for this Invoice.',
        cancelButtonText: 'Assign Single Period',
        confirmButtonText: 'Assign Multiple Periods',
        cancelColor: 'info',
        confirmColor: 'info',
        width: '500px',
        onClose,
      });
      await handleAttachment(!isSinglePeriodAccepted);
    },
    [confirm, onClose]
  );

  // handle attachments
  const handleAttachDocument = useCallback(async () => {
    if (noOfAttachments) {
      await confirm({
        title: 'Multiple Attachments',
        content: multipleAttachmentsContent,
        confirmButtonText: 'Attach and Continue',
        cancelButtonText: 'Cancel',
        cancelColor: 'info',
        width: '80%',
      }).then(async (isAttachClicked) => {
        if (isAttachClicked) {
          if (hasPoMpa) {
            handleAttachDocumentWithMpa(handleAttachment);
            closeModal();
          } else {
            handleAttachment(true);
            closeModal();
          }
        }
      });
    } else {
      if (hasPoMpa) {
        handleAttachDocumentWithMpa(handleAttachment);
        closeModal();
      } else {
        handleAttachment(true);
        closeModal();
      }
    }
  }, [
    noOfAttachments,
    confirm,
    hasPoMpa,
    handleAttachDocumentWithMpa,
    handleAttachment,
    closeModal,
  ]);

  const handleWithoutAttachDocument = () => {
    if (hasPoMpa) {
      confirm({
        title: 'Multiple Period Allocation',
        content:
          'The PO associated with this Invoice is assigned to Multiple Periods. Please choose either Multiple or Single Period Assignment for this Invoice.',
        cancelButtonText: 'Assign Single Period',
        confirmButtonText: 'Assign Multiple Periods',
        cancelColor: 'info',
        confirmColor: 'info',
        width: '500px',
        onClose,
      }).then(async (useMultiplePeriod: boolean) => {
        handleWithoutAttachment(!useMultiplePeriod);
      });
    } else {
      handleWithoutAttachment(true);
    }
    closeModal();
  };

  const onRowDoubleClick = (event: GridRowDoubleClickEvent) => {
    const key = event.dataItem[DATA_ITEM_KEY];
    setAttachmentListData((prev) => {
      const lineItems = [...prev].map((item) => {
        if (item.attachId === key) {
          return { ...item, selected: false };
        } else return item;
      });
      return lineItems;
    });
  };
  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters.length,
    [dataState.filter?.filters.length]
  );

  return (
    <Dialog width={'80%'} title={title} open={openModal} onClose={closeModal} id="modal">
      {isInvoice ? (
        <div className="font-bold text-[13px] pb-1">
          {isExistingDocument ? (
            <p>Select documents to attach to the existing Invoice/Credit Memo:</p>
          ) : isNewDocument ? (
            <>
              <p>A new Invoice/Credit Memo will be created.</p>
              <p>Select documents to attach to the new Invoice/Credit Memo:</p>
            </>
          ) : (
            <>
              <p>A new Invoice/Credit Memo will be created for the selected PO.</p>
              <p>Select documents to attach to the new Invoice/Credit Memo:</p>
            </>
          )}
        </div>
      ) : (
        <div className="font-bold text-[13px] pb-1">
          {isExistingDocument ? (
            <p>Select documents to attach to the existing receipt:</p>
          ) : isNewDocument ? (
            <>
              <p>A new receipt will be created.</p>
              <p>Select documents to attach to new receipt:</p>
            </>
          ) : (
            <>
              <p>A new receipt will be created for the selected PO.</p>
              <p>Select documents to attach to new receipt:</p>
            </>
          )}
        </div>
      )}

      <GridWrapper
        id="attachDocumentGrid"
        columns={documentAttachmentColumns}
        dataSource={attachmentListData}
        dataState={dataState}
        {...defaultProps}
        height={'60vh'}
        total={count}
        DataItemKey={DATA_ITEM_KEY}
        selectable={selectable}
        onDataStateChange={onDataStateChange}
        onSelectionChange={onSelectionChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
        gridToolbarChildren={
          <InvoiceGridToolbar
            clearFilter={handleClearFilter}
            isClearFiltersDisabled={isClearFiltersDisabled}
          />
        }
        loading={isLoading}
        onRowDoubleClick={onRowDoubleClick}
      />
      <DialogActionsBar>
        <div className="flex justify-end gap-2">
          {isRowSelected ? (
            <Button color="info" onClick={handleAttachDocument} loading={isAttachmentButtonLoading}>
              Attach and Continue
            </Button>
          ) : (
            <Button
              color="info"
              onClick={handleWithoutAttachDocument}
              loading={isAttachmentButtonLoading}
            >
              Continue without Attachment
            </Button>
          )}
          <Button color="info" onClick={closeModal}>
            Cancel
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default AttachDocumentWizard;
